import {
    CameraOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { App, Button, Modal, Popconfirm, Upload } from "antd";
import type { RcFile } from "antd/es/upload";
import imageCompression from "browser-image-compression";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSwipeable } from "react-swipeable";

import logoUrl from "@/assets/icons/logo_monochrome.svg";
import { useDeviceOrientation } from "@/hooks/device/useDeviceOrientation";
import { useOrientationLimits } from "@/hooks/device/useOrientationLimits";
import { useAppSelector, useOfflineModeSelector } from "@/hooks/redux";
import { photoStorage } from "@/Services/PhotoStorage";
import { getUploadPhotoMeta, uploadPhoto } from "@/Services/UploadPhoto";
import { PrimaryText } from "@/shared/ui/Typography";
import { PhotoCaptions } from "@/types/common";
import { isOnline } from "@/utils/network/isOnline";

import { Camera, CameraProps } from "./Camera";
import { addCaptionBR } from "./GalleryUploader.utils";
import {
    StyledButton,
    StyledDeleteButton,
    StyledGalleryUploader,
    StyledModalControllers,
    StyledModalFooter,
    StyledPhotoCard,
    StyledPhotoList,
    StyledPhotoPreview,
    StyledPhotosContainer,
    StyledPreviewImage,
    StyledUploader,
} from "./Styled";

const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const DATE_FORMAT = "DD.MM.YYYY HH:mm:ss";
const MAX_MB = 3;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const MAX_ALLOWED_LATENCY_MS = 2000;

interface GalleryPhoto {
    id: string;
    date: string;
    previewUrl: string;
    name: string;
}

interface PreviewPhoto extends GalleryPhoto {
    index: number;
}

interface GalleryUploaderProps {
    previewTitle?: string;
    onUpload: (photo: string) => void;
    onDelete: (photoId: string) => void;
    pointId: string;
    savedPhotos: string[];
    activeItemId?: string;
    disabled?: boolean;
    captionLines?: PhotoCaptions;
    cameraProps?: Omit<
        CameraProps,
        "open" | "onClose" | "onCapture" | "orientation"
    >;
    galleryConfig?: {
        skipUpload?: boolean;
    };
}

export const GalleryUploader: FC<GalleryUploaderProps> = ({
    previewTitle = "Title",
    onUpload,
    onDelete,
    activeItemId,
    pointId,
    savedPhotos,
    disabled = false,
    captionLines,
    cameraProps = {},
    galleryConfig = {},
}) => {
    const { notification } = App.useApp();

    const offlineMode = useOfflineModeSelector();
    const cameraSource = useAppSelector((s) => s.config.cameraSource);

    const [photoList, setPhotoList] = useState<GalleryPhoto[]>([]);
    const [previewImage, setPreviewImage] = useState<PreviewPhoto | null>(null);
    const [loading, setLoading] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [levelEnabled, setLevelEnabled] = useState(false);

    const limits = useOrientationLimits();

    const orientation = useDeviceOrientation({
        enabled: levelEnabled,
        limits,
    });

    const { skipUpload = false } = galleryConfig;

    useEffect(() => {
        const loadPhotosFromDB = async () => {
            try {
                setLoading(true);

                const photos = await photoStorage.getPhotosByIds(savedPhotos);

                const loadedPhotos: GalleryPhoto[] = photos.map((photo) => ({
                    id: photo.id,
                    date: photo.createdAt,
                    previewUrl: photo.base64,
                    name: photo.name,
                }));

                setPhotoList(loadedPhotos.reverse());
            } catch (error) {
                console.error("Error loading photos from IndexedDB:", error);
                notification.error({
                    title: "Помилка завантаження фото з бази даних",
                    description: String(error),
                });
            } finally {
                setLoading(false);
            }
        };

        loadPhotosFromDB();
    }, [notification, activeItemId, savedPhotos]);

    const handleCloseModal = useCallback(() => setPreviewImage(null), []);

    const handlePreview = useCallback((photo: PreviewPhoto) => {
        setPreviewImage(photo);
    }, []);

    const handleBeforeUpload = useCallback(
        async (file: File) => {
            try {
                setLoading(true);

                const isValidType = ACCEPTED_FILE_TYPES.includes(file.type);
                if (!isValidType) {
                    notification.error({
                        title: "Ви можете завантажити тільки JPG/PNG/GIF файли!",
                    });
                    return Upload.LIST_IGNORE;
                }

                // Wait until IDB is ready (gate closes when app goes to background
                // and opens again after reconnect on visibility restore).
                await photoStorage?.waitUntilReady?.();

                const { url, fromSlot } = await getUploadPhotoMeta({
                    pointId,
                    skipUpload,
                });

                const photoId = url;

                const exists = await photoStorage.photoExists(photoId);
                if (exists) return Upload.LIST_IGNORE;

                const photoDate = dayjs();

                let finalFile = file;

                if (captionLines) {
                    const captionsWithTime = {
                        topLines: [
                            captionLines.topLines[0],
                            `${captionLines.topLines[1]}, ${photoDate.format(
                                DATE_FORMAT,
                            )}`,
                        ] as [string, string],
                        bottomLines: captionLines.bottomLines,
                    };

                    const blobWithCaption = await addCaptionBR(
                        file,
                        captionsWithTime,
                        logoUrl,
                    );

                    const baseName = file.name.replace(
                        /\.(jpe?g|png|webp|gif)$/i,
                        "",
                    );

                    finalFile = new File([blobWithCaption], `${baseName}.jpg`, {
                        type: "image/jpeg",
                    });
                }

                if (finalFile.size > MAX_BYTES) {
                    try {
                        const compressedFile = await imageCompression(
                            finalFile,
                            {
                                maxSizeMB: MAX_MB,
                                useWebWorker: true,
                                alwaysKeepResolution: false,
                            },
                        );

                        if (compressedFile.size <= finalFile.size) {
                            finalFile = compressedFile;
                        }
                    } catch (err) {
                        console.error("imageCompression failed:", err);
                    }
                }

                await photoStorage.savePhoto({
                    id: photoId,
                    file: finalFile,
                    fromSlot,
                    skipUpload,
                });

                const base64 = await photoStorage.getPhoto(photoId);
                if (!base64) return Upload.LIST_IGNORE;

                const online = await isOnline(MAX_ALLOWED_LATENCY_MS);

                if (!offlineMode && online && !skipUpload) {
                    uploadPhoto(photoId);
                }

                const newPhoto: GalleryPhoto = {
                    id: photoId,
                    date: photoDate.toString(),
                    previewUrl: base64,
                    name: finalFile.name,
                };

                setPhotoList((prev) => [newPhoto, ...prev]);
                onUpload(photoId);
            } catch (e) {
                const name: string =
                    typeof e === "object" && e !== null && "name" in e
                        ? String((e as { name: unknown }).name ?? "")
                        : "";

                const message: string =
                    typeof e === "object" && e !== null && "message" in e
                        ? String((e as { message: unknown }).message ?? "")
                        : String(e ?? "");

                const isQuota =
                    name === "QuotaExceededError" ||
                    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
                    /quota/i.test(message) ||
                    /storage/i.test(message);

                if (isQuota) {
                    notification.error({
                        title: "Неможливо зберегти фотографію. На пристрої недостатньо місця. Звільніть його",
                    });
                    return;
                }

                const isConnectionError =
                    name === "InvalidStateError" ||
                    name === "TransactionInactiveError" ||
                    name === "UnknownError" ||
                    /connection is closing/i.test(message) ||
                    /database.*clos/i.test(message) ||
                    /internal error/i.test(message) ||
                    /connection to indexed database server lost/i.test(message);

                if (isConnectionError) {
                    notification.error({
                        title: "Втрачено з'єднання з базою даних",
                        description: "Будь ласка, перезавантажте сторінку",
                    });
                    return;
                }

                const isCanvasError =
                    /toBlob/i.test(message) ||
                    /could not get 2d context/i.test(message) ||
                    /out of memory/i.test(message);

                if (isCanvasError) {
                    notification.error({
                        title: "Неможливо обробити фотографію. На пристрої недостатньо пам'яті",
                    });
                    return;
                }

                notification.error({
                    title: "Неможливо зберегти фотографію. Можливо на пристрої недостатньо місця",
                    description: message || String(e),
                });
            } finally {
                setLoading(false);
            }

            return Upload.LIST_IGNORE;
        },
        [
            notification,
            pointId,
            skipUpload,
            captionLines,
            offlineMode,
            onUpload,
        ],
    );

    const handleCameraCapture = useCallback(
        async (blob: Blob) => {
            const file = new File([blob], `camera_${Date.now()}.jpg`, {
                type: "image/jpeg",
            }) as RcFile;

            setCameraOpen(false);

            await handleBeforeUpload(file);
        },
        [handleBeforeUpload],
    );

    const isLast = useMemo(
        () => previewImage?.index === photoList.length - 1,
        [previewImage, photoList.length],
    );

    const isFirst = useMemo(() => previewImage?.index === 0, [previewImage]);

    const handlerNext = useCallback(() => {
        if (!previewImage || isLast) return;

        const nextIndex = previewImage.index + 1;
        const nextPhoto = photoList[nextIndex];

        if (nextPhoto) {
            setPreviewImage({ ...nextPhoto, index: nextIndex });
        }
    }, [previewImage, isLast, photoList]);

    const handlerPrevious = useCallback(() => {
        if (!previewImage || isFirst) return;

        const prevIndex = previewImage.index - 1;
        const prevPhoto = photoList[prevIndex];

        if (prevPhoto) {
            setPreviewImage({ ...prevPhoto, index: prevIndex });
        }
    }, [previewImage, isFirst, photoList]);

    const actionsSwipeHandlers = useSwipeable({
        onSwipedLeft: handlerNext,
        onSwipedRight: handlerPrevious,
        trackTouch: true,
        preventScrollOnSwipe: true,
    });

    const handleRemove = useCallback(async () => {
        if (!previewImage) return;

        try {
            const idToRemove = previewImage.id;
            const removedIndex = previewImage.index;

            const photoInfo = await photoStorage.getPhotoInfo(idToRemove);

            if (photoInfo?.fromSlot || photoInfo?.uploaded) {
                await photoStorage.createSlot({ url: photoInfo.id });
            }

            await photoStorage.deletePhoto(idToRemove);

            setPhotoList((prev) => prev.filter((p) => p.id !== idToRemove));

            onDelete(idToRemove);

            notification.success({
                title: "Фото успішно видалено",
            });

            const newPhotoList = photoList.filter((p) => p.id !== idToRemove);
            if (newPhotoList.length > 0) {
                const newIndex = Math.min(
                    removedIndex,
                    newPhotoList.length - 1,
                );
                const nextPhoto = newPhotoList[newIndex];
                if (nextPhoto) {
                    setPreviewImage({ ...nextPhoto, index: newIndex });
                } else {
                    handleCloseModal();
                }
            } else {
                handleCloseModal();
            }
        } catch (error) {
            console.error("Error deleting photo from IndexedDB:", error);
            notification.error({
                title: "Помилка видалення фото",
            });
        }
    }, [notification, previewImage, handleCloseModal, onDelete, photoList]);

    useEffect(() => {
        if (!previewImage) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                handlerPrevious();
            } else if (e.key === "ArrowRight") {
                handlerNext();
            } else if (e.key === "Escape") {
                handleCloseModal();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [previewImage, handlerNext, handlerPrevious, handleCloseModal]);

    const handleCameraOpen = useCallback(async () => {
        const ok = await orientation.enable();
        setLevelEnabled(ok);
        setCameraOpen(true);
    }, [orientation]);

    return (
        <StyledGalleryUploader>
            {photoList.length > 0 && (
                <StyledPhotosContainer $hasPhotos={photoList.length > 0}>
                    <StyledPhotoList>
                        {photoList.map((photo, index) => (
                            <StyledPhotoCard
                                key={photo.id}
                                onClick={() =>
                                    handlePreview({ ...photo, index })
                                }
                                cover={
                                    <StyledPhotoPreview>
                                        <img
                                            src={photo.previewUrl}
                                            alt={photo.name}
                                            loading="lazy"
                                        />
                                    </StyledPhotoPreview>
                                }
                            />
                        ))}
                    </StyledPhotoList>
                </StyledPhotosContainer>
            )}

            <StyledUploader>
                {cameraSource === "native" ? (
                    <Upload
                        listType="picture-card"
                        fileList={[]}
                        beforeUpload={handleBeforeUpload}
                        multiple
                        accept="image/*"
                        capture="environment"
                        showUploadList={false}
                        disabled={loading || disabled}
                    >
                        <CameraOutlined />
                    </Upload>
                ) : (
                    <Button
                        type={"text"}
                        onClick={handleCameraOpen}
                        disabled={loading || disabled}
                    >
                        <CameraOutlined />
                    </Button>
                )}
            </StyledUploader>

            {previewImage && (
                <Modal
                    open
                    title={previewTitle}
                    onCancel={handleCloseModal}
                    centered
                    styles={{
                        mask: {
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                        },
                    }}
                    footer={
                        <StyledModalFooter>
                            <PrimaryText>
                                {dayjs(previewImage.date).format(DATE_FORMAT)}
                            </PrimaryText>

                            <Popconfirm
                                title="Ви впевнені? Дія є незворотною"
                                onConfirm={handleRemove}
                                okText="Так"
                                cancelText="Ні"
                            >
                                <StyledDeleteButton
                                    type="text"
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                        </StyledModalFooter>
                    }
                >
                    <StyledPreviewImage
                        alt="preview"
                        src={previewImage.previewUrl}
                        {...actionsSwipeHandlers}
                    />

                    <StyledModalControllers>
                        <StyledButton
                            type="text"
                            icon={<LeftOutlined />}
                            onClick={handlerPrevious}
                            disabled={isFirst}
                        />
                        <StyledButton
                            type="text"
                            icon={<RightOutlined />}
                            onClick={handlerNext}
                            disabled={isLast}
                        />
                    </StyledModalControllers>
                </Modal>
            )}

            <Camera
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handleCameraCapture}
                orientation={orientation}
                {...cameraProps}
            />
        </StyledGalleryUploader>
    );
};
